import { Box, Item, Text } from '@factorialco/deprecated-design-system'
import { ConceptsFragment, SettingsStepsLegalEntityFragment } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { LevelItem } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/LevelItem'
import { upsertCompensationMappings } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/helper'
import { DatevMapping, DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

interface SalaryTypesByLegalEntityProps {
  data: DatevWizardData
  onMappingChanged: (mappings: DatevMapping[]) => void
  legalEntity: SettingsStepsLegalEntityFragment
  payrollConcepts: ConceptsFragment[]
}

const WAGE_TYPES = {
  FIXED: 'Fixed',
  HOURLY: 'Hourly',
}

export const SalaryTypesByLegalEntity = ({
  legalEntity,
  payrollConcepts,
  data,
  onMappingChanged,
}: SalaryTypesByLegalEntityProps) => {
  const legalEntityName = legalEntity.legalName

  const salaryTypeOptions: Item[] = JSON.parse(
    data.answers[`salary_types_${legalEntity.id}`] ?? '[]'
  )
  const mappings: DatevMapping[] = data.sources?.compensations?.mappings ?? []
  const legalEntityId = legalEntity.id.toString()
  const mappingsById = keyBy(
    mappings.filter((mapping) => mapping?.meta?.legal_entity_id === legalEntityId),
    'factorial_id'
  )

  const modifiedSalaryTypeOptions = salaryTypeOptions.map((option) => ({
    value: option.value,
    label: `${option.value} - ${option.label}`,
  }))

  const findSelection = (compensationId: string) => {
    const mapping = mappingsById[compensationId]

    if (!mapping) return ''
    return (
      modifiedSalaryTypeOptions.find((option) => option.value === mapping.datev_id)?.label || ''
    )
  }

  const fixedWage = WAGE_TYPES.FIXED
  const hourlyWage = WAGE_TYPES.HOURLY

  const salaryFrequencies: { [key: string]: string } = {
    [i18n.t('common.fixed')]: fixedWage,
    [i18n.t('common.hourly')]: hourlyWage,
  }

  const baseSalaryTypes: { id: string; name: string }[] = Object.keys(salaryFrequencies).map(
    (key) => ({
      id: salaryFrequencies[key] ?? '',
      name: key,
    })
  )

  const setFormValues = (value: string, itemId: string) => {
    const newMappings = upsertCompensationMappings(mappings, value, itemId, legalEntityId)
    onMappingChanged(newMappings)
  }

  return (
    <Box gap="s24">
      {legalEntityName && <Text.Header2>{legalEntityName}</Text.Header2>}
      {baseSalaryTypes.map((type) => (
        <LevelItem
          key={type.id}
          name={type.name}
          selectedValue={findSelection(type.id)}
          options={modifiedSalaryTypeOptions}
          onSelect={(value, _label) => setFormValues(value, type.id)}
        />
      ))}
      {payrollConcepts.map((concept) => (
        <LevelItem
          key={concept.id.toString()}
          name={concept.translatedName || concept.label}
          selectedValue={findSelection(concept.id.toString())}
          options={modifiedSalaryTypeOptions}
          onSelect={(value, _label) => setFormValues(value, concept.id.toString())}
        />
      ))}
    </Box>
  )
}
